'use client'

import { useEffect } from 'react'

interface AutoLogoutWarningProps {
  isVisible: boolean
  timeLeft: number
  onExtendSession: () => void
  formatTimeLeft: (seconds: number) => string
}

export default function AutoLogoutWarning({ 
  isVisible, 
  timeLeft, 
  onExtendSession, 
  formatTimeLeft 
}: AutoLogoutWarningProps) {
  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isVisible) return

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onExtendSession()
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        onExtendSession()
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [isVisible, onExtendSession])

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-pulse">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Session Timeout Warning
              </h3>
              <p className="text-sm text-gray-500">
                Your session will expire soon
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              You will be automatically logged out due to inactivity in:
            </p>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">
                {formatTimeLeft(timeLeft)}
              </div>
              <p className="text-sm text-gray-500">
                Click "Stay Logged In" to continue your session
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${(timeLeft / (5 * 60)) * 100}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onExtendSession}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Stay Logged In
            </button>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400">
              Press <kbd className="px-1 py-0.5 bg-gray-100 border rounded text-xs">Enter</kbd> or 
              <kbd className="px-1 py-0.5 bg-gray-100 border rounded text-xs ml-1">Space</kbd> to stay logged in
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

